import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

/** Decodifica il payload del JWT senza verifica della firma. */
function decodeJwtPayload(): { id: number; ruolo: string } | null {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

interface SedeBase {
  id: number;
  nome: string;
  societaId: number;
  sedeCommerciale?: boolean;
}

interface UseSediUtenteResult {
  sedi: SedeBase[];
  loading: boolean;
  hasSediMultiple: boolean;
}

/**
 * Hook che restituisce le sedi visibili per l'utente loggato,
 * filtrate per societaId.
 *
 * - SUPERADMIN: tutte le sedi della società (da /api/sedi)
 * - UTENTE operativo: solo le sedi associate in UtenteSede (da /api/utenti/:id/sedi)
 *
 * La discriminazione avviene decodificando il JWT dal localStorage.
 *
 * Parametri:
 *   societaId — filtra per questa società; se 0/null non carica nulla
 *   sedeCommercialeOnly — se true filtra solo sedi commerciali (default false)
 */
export function useSediUtente(
  societaId: number | null | undefined,
  sedeCommercialeOnly = false
): UseSediUtenteResult {
  const [sedi, setSedi] = useState<SedeBase[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!societaId) { setSedi([]); return; }

    let cancelled = false;
    setLoading(true);

    const user = decodeJwtPayload();
    const isOperativo = user?.ruolo === 'UTENTE';

    const doFetch = async () => {
      try {
        let risultato: SedeBase[] = [];

        if (isOperativo && user) {
          // Operativo: solo le sue sedi
          const d = await apiFetch<{ sedi: SedeBase[] }>(`/api/utenti/${user.id}/sedi`);
          risultato = (d.sedi ?? []).filter(s => s.societaId === societaId);
        } else {
          // Admin / Superadmin: tutte le sedi della società
          const d = await apiFetch<{ sedi: SedeBase[] }>(`/api/sedi?societaId=${societaId}`);
          risultato = d.sedi ?? [];
        }

        if (sedeCommercialeOnly) {
          risultato = risultato.filter(s => s.sedeCommerciale);
        }

        if (!cancelled) setSedi(risultato);
      } catch {
        if (!cancelled) setSedi([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    doFetch();
    return () => { cancelled = true; };
  }, [societaId, sedeCommercialeOnly]);

  return { sedi, loading, hasSediMultiple: sedi.length > 1 };
}
